import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { StatusBadgeComponent } from '../../components/status-badge/status-badge.component';
import { DynamicsDataService } from '../../services/dynamics-data.service';

@Component({
  selector: 'app-branch-detail',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, StatusBadgeComponent],
  template: `
    @if (dataService.branchLoading() && !dataService.branchIndex()) {
      <div class="loading">Loading...</div>
    }

    @if (dataService.branchError(); as err) {
      <div class="error">{{ err }}</div>
    }

    @if (dataService.branchIndex(); as b) {
      <div class="header">
        <h2>{{ b.branchName }}</h2>
        <div class="meta">
          <span>
            Commit:
            <a [href]="commitUrl()" target="_blank" rel="noopener" class="link mono">
              {{ b.apps[0]?.commitSha?.slice(0, 7) }}
            </a>
          </span>
          @if (b.apps[0]?.pipelineUrl) {
            <span>
              <a [href]="b.apps[0].pipelineUrl" target="_blank" rel="noopener" class="link">
                View pipeline
              </a>
            </span>
          }
          <span>Actor: {{ b.apps[0]?.actor }}</span>
        </div>
      </div>

      <div class="apps-grid">
        @for (app of b.apps; track app.appName) {
          <div class="app-card">
            <div class="app-card-header">
              <span class="app-name">{{ app.appName }}</span>
              <app-status-badge [status]="app.status" />
            </div>
            <div class="app-card-body">
              <div class="field">
                <span class="label">Config</span>
                <span class="mono">{{ app.buildConfig }}</span>
              </div>

              @if (app.queuedAt) {
                <div class="field">
                  <span class="label">Queued</span>
                  <span>{{ app.queuedAt | date: 'medium' }}</span>
                </div>
              }

              @if (app.buildDurationMs) {
                <div class="field">
                  <span class="label">Build time</span>
                  <span>{{ formatDuration(app.buildDurationMs) }}</span>
                </div>
              }

              @if (app.bundleSizeBytes) {
                <div class="field">
                  <span class="label">Bundle size</span>
                  <span>{{ formatBytes(app.bundleSizeBytes) }}</span>
                </div>
              }

              @if (app.deployedAt) {
                <div class="field">
                  <span class="label">Deployed</span>
                  <span>{{ app.deployedAt | date: 'medium' }}</span>
                </div>
              }

              @if (app.url) {
                <div class="field">
                  <span class="label">URL</span>
                  <a [href]="app.url" target="_blank" rel="noopener" class="link">
                    {{ app.url }}
                  </a>
                </div>
              }

              @if (app.error) {
                <div class="field error-field">
                  <span class="label">Error</span>
                  <span class="error-text">{{ app.error }}</span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    }
  `,
  styleUrl: './branch-detail.component.less',
})
export class BranchDetailComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  protected readonly dataService = inject(DynamicsDataService);

  protected readonly commitUrl = computed(() => {
    const app = this.dataService.branchIndex()?.apps[0];
    if (!app) return '';
    return `https://github.com/Recruitee/recruitee/commit/${app.commitSha}`;
  });

  public ngOnInit(): void {
    const dynamicId = this.route.snapshot.paramMap.get('dynamicId')!;
    this.dataService.subscribeToBranch(dynamicId);
  }

  public ngOnDestroy(): void {
    this.dataService.unsubscribeFromBranch();
  }

  protected formatDuration(ms: number): string {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes > 0 ? `${minutes}m ${remainingSeconds}s` : `${seconds}s`;
  }

  protected formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  }
}
